import {
  AnalyticsProvider,
  CONTENT_ELEMENT_DISPLAYED,
  CONTENT_ELEMENT_PRESSED_EVENT,
  PROMOTIONAL_BANNER_DISPLAYED,
  tealiumAdapter,
} from '@seek/cmsu-analytics';
import type {
  GetPagesQuery,
  GetPagesWithEntitiesQuery,
} from '@seek/cmsu-cms-connect';
import { PageComponent, useTargetBlank } from '@seek/cmsu-components';
import type { AutofillableValue } from '@seek/cmsu-components/src/modules/CForm/types';

import { useAuth, useConfig } from 'src/client/shared/hooks';
import { usePageTrackingData } from 'src/client/shared/hooks/usePageTracking';
import { getAutofillableValue } from 'src/client/shared/utils/getAutofillableValue';
import { getLocales } from 'src/client/shared/utils/getLocales';
import { getSectionsData } from 'src/client/shared/utils/helper';
import { getPreviewDocumentLink } from 'src/helpers/loadersHelper';
import { transformSeoData } from 'src/helpers/seo';

import { WPT_SALESFORCE_API } from '../../../config';
import { pageRenderers } from '../../../helpers/pageRenderers';
import type { LayoutProps } from '../../shared/types';
import { PageLayout } from '../PageLayout/PageLayout';

const layoutProps: LayoutProps = {
  title: 'Loading..',
};

export const PageComponentWrapper = ({
  page,
  melwaysLocale,
  fullURL,
  enabledSubscriptionForm,
}: {
  page: GetPagesQuery['pages'][0];
  melwaysLocale: string;
  fullURL: string;
  enabledSubscriptionForm?: boolean;
}) => {
  const {
    title,
    slug,
    banner,
    relatedPageSection,
    seo,
    localizations,
    sites,
    promotionalBanner,
  } = page;

  const getBannerImage = () => {
    switch (banner?.__typename) {
      case 'CBannerPrimary':
        return banner?.BannerPrimary_image?.url;
      case 'CBannerAward':
        return banner?.backgroundImage?.url;
      case 'CBannerShopfront':
        return banner?.image?.url;
      default:
        return '';
    }
  };

  const bannerImage = getBannerImage();

  const promotionalBannerTrackingData = {
    [promotionalBanner?.id || '']: {
      eventName: PROMOTIONAL_BANNER_DISPLAYED,
    },
  };
  const pageSeo = transformSeoData(seo || {}, {
    title,
    image: bannerImage,
  });
  useTargetBlank();

  const config = useConfig();
  const {
    privacyUrl,
    sourceName,
    country,
    language,
    site,
    pathname,
    utmParameters,
    recaptchaKey,
  } = useConfig();

  const locales = getLocales(
    site,
    pathname.replace(`/${language}/`, ''),
    sites,
    localizations,
  );

  const section = relatedPageSection || {};

  const currentPage = page.title.toLocaleLowerCase();
  const trackingData = usePageTrackingData(currentPage);
  page.containers.forEach((container) => {
    container.blocks?.forEach((block) => {
      block.items.forEach((item) => {
        if (item.__typename === 'CBanner') {
          if (item.banner_items?.__typename === 'CBannerArticle') {
            const article = item.banner_items.article;
            if (article) {
              const featureArticleData = {
                id: article?.id,
                isArticle: true,
                title: article?.title,
                slug: article?.slug,
                locale: page.locale,
                site,
                link: '',
                sectionName:
                  article?.category?.relatedCategorySection?.sectionName,
                parentSectionName:
                  article?.category?.relatedCategorySection?.parentSection
                    ?.sectionName,
                stage: undefined,
              };
              if (item.banner_items?.article) {
                item.banner_items.article.url =
                  getPreviewDocumentLink(config, featureArticleData) || '';
              }
            }
          }
        }
      });
    });
  });
  const sectionDetails =
    relatedPageSection as GetPagesWithEntitiesQuery['pages'][0]['relatedPageSection'];
  const sectionData = getSectionsData(sectionDetails);

  const { caAccountContext, hirerAccountContext } = useAuth();
  const autofillableValue: AutofillableValue = getAutofillableValue(
    caAccountContext,
    hirerAccountContext,
  );

  return (
    <AnalyticsProvider
      analytics={{
        data: { ...trackingData, ...sectionData },
        tealiumAdapter,
        events: {
          'button-link': {
            eventName: CONTENT_ELEMENT_PRESSED_EVENT,
          },
          'text-link': {
            eventName: CONTENT_ELEMENT_PRESSED_EVENT,
          },
          'image-link': {
            eventName: CONTENT_ELEMENT_PRESSED_EVENT,
          },
          'dropdown-link': {
            eventName: CONTENT_ELEMENT_PRESSED_EVENT,
          },
          'dropdown-list': {
            eventName: CONTENT_ELEMENT_PRESSED_EVENT,
          },
          'accordion-label': {
            eventName: CONTENT_ELEMENT_PRESSED_EVENT,
          },
          'disclosure-label': {
            eventName: CONTENT_ELEMENT_PRESSED_EVENT,
          },
          'form-submission': {
            eventName: CONTENT_ELEMENT_DISPLAYED,
          },
          'datalab-elements': {
            eventName: CONTENT_ELEMENT_PRESSED_EVENT,
          },
          ...(promotionalBanner?.id && promotionalBannerTrackingData),
        },
      }}
    >
      <PageLayout
        layoutProps={{
          ...layoutProps,
          title,
          section,
          seo: pageSeo,
          categoryName: slug,
          locales,
        }}
      >
        <PageComponent
          language={language}
          melwaysLocale={melwaysLocale}
          page={page}
          fullURL={fullURL}
          enabledSubscriptionForm={enabledSubscriptionForm}
          renderers={pageRenderers}
          subscriptionFormPrivacyUrl={privacyUrl}
          subscriptionFormSourceName={sourceName}
          country={country}
          wptSalesForceApi={WPT_SALESFORCE_API}
          utmParameters={utmParameters}
          recaptchaKey={recaptchaKey}
          privacyUrl={privacyUrl}
          sourceName={sourceName}
          site={site}
          autofillableValue={autofillableValue}
        />
      </PageLayout>
    </AnalyticsProvider>
  );
};
